import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { ExtendedMap, WidgetParams } from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { store } from '../../../jmkw.store';
import { WidgetControlBarComponent } from './widget-control-bar/widget-control-bar.component';
import { WidgetFactoryService } from './widget-factory.service';

@Component({
  selector: 'jmkw-widget-wrapper',
  standalone: true,
  imports: [CommonModule, WidgetControlBarComponent],
  templateUrl: './widget-wrapper.component.html',
  styleUrl: './widget-wrapper.component.scss',
  providers: [WidgetFactoryService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WidgetWrapperComponent implements OnDestroy {
  private readonly _widgetFactory = inject(WidgetFactoryService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _logger = inject(NGXLogger);

  private _destroyed = false;

  controlBarContent?: TemplateRef<unknown>;
  expanded = true;

  @Input() widgetParams?: WidgetParams;
  @Input() id?: string;

  @ViewChild('widgetHost', { read: ViewContainerRef }) set widgetHost(
    viewContainerRef: ViewContainerRef | null
  ) {
    if (!viewContainerRef) {
      return;
    }

    this._initializeWidget(viewContainerRef);
  }
  private readonly _destroyRef = inject(DestroyRef);

  constructor() {
    // Setup cleanup on destruction (navigation)
    this._destroyRef.onDestroy(() => {
      this._logger.debug(
        '[WidgetWrapperComponent] Destroyed by navigation, cleaning up'
      );
      // Set widgetHost to null to ensure proper cleanup
      this._widgetFactory.widgetHost = null;
    });
  }

  private _initializeWidget(viewContainerRef: ViewContainerRef): void {
    try {
      // Set the widget host
      this._widgetFactory.widgetHost = viewContainerRef;

      // Create the widget instance
      const success = this._widgetFactory.instantiateWidget(
        this.id,
        this.widgetParams
      );
      if (!success) {
        this._logger.error(
          `[WidgetWrapperComponent] Failed to instantiate widget ${this.id}`
        );
        return;
      }

      // Get the widget reference
      const widgetRef = this._widgetFactory.widgetComponentRef;
      if (!widgetRef?.instance) {
        this._logger.error(
          `[WidgetWrapperComponent] Widget reference or instance is null for ${this.id}`
        );
        return;
      }

      // Set control bar content
      this.controlBarContent = widgetRef.instance.controlBarContent;

      // Schedule change detection if component is not destroyed
      if (!this._destroyed) {
        setTimeout(() => this._cdr.detectChanges());
      }
    } catch (error) {
      this._logger.error(
        `[WidgetWrapperComponent] Error initializing widget:`,
        error
      );
      // Handle error appropriately - maybe show error state in UI
    }
  }

  toggleExpanded(expanded: boolean): void {
    if (this._destroyed) {
      return;
    }

    this.expanded = expanded;
    this._cdr.detectChanges();
  }

  closeWidget(): void {
    if (!this.id) {
      this._logger.warn(
        '[WidgetWrapperComponent] Attempted to close widget with no ID'
      );
      return;
    }

    this._logger.trace(`[WidgetWrapperComponent] closeWidget(${this.id})`);

    try {
      // Get current widget instance
      const widgetRef = this._widgetFactory.widgetComponentRef;
      if (!widgetRef?.instance) {
        this._logger.warn(
          `[WidgetWrapperComponent] No widget instance found for ${this.id}`
        );
        return;
      }

      // Call closeWidget on component instance if it exists
      const component = widgetRef.instance;
      if (component.closeWidget) {
        component.closeWidget();
      }

      // Update store
      const currentWidgets = store.activeWidgetMap();
      const remainingWidgets = new Map(
        Array.from(currentWidgets.entries()).filter(([key]) => key !== this.id)
      );

      this._logger.debug(
        `[WidgetWrapperComponent] Remaining widgets:`,
        Array.from(remainingWidgets.entries())
      );

      store.activeWidgetMap.set(new ExtendedMap(remainingWidgets));

      // Clean up component reference
      this._widgetFactory.destroyWidget();
    } catch (error) {
      this._logger.error(
        `[WidgetWrapperComponent] Error closing widget:`,
        error
      );
      // Handle error appropriately - maybe show error state iPn UI
    }
  }

  ngOnDestroy(): void {
    this._destroyed = true;

    try {
      // Clean up widget if it exists
      if (this._widgetFactory.widgetComponentRef) {
        this._widgetFactory.destroyWidget();
      }
    } catch (error) {
      this._logger.error(
        `[WidgetWrapperComponent] Error destroying widget:`,
        error
      );
    }
  }
}
